/**
 * @file Provides custom `axios` instance and Orval client functions.
 */

import axios from "axios"
import Cookies from "js-cookie"

import {
  SESSION_TOKEN_STORAGE_KEY,
  Client,
  CSRF_COOKIE_NAME,
} from "@/lib/constants"
import { recordIdleSecondsRemaining } from "@/lib/session"
import { isEmbed } from "@/lib/utils"

import type {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios"

/**
 * Internal session token memory storage.
 *
 * DRF uses X-Session-Token authentication from django-allauth headless.
 *
 * Setting the token in sessionStorage keeps the token across page reloads
 * but does not share it across browser tabs or windows, unlike localStorage.
 * This allows to persist the token while keeping each tab’s state isolated.
 */
export function setSessionToken(token: string | null) {
  if (token) {
    sessionStorage.setItem(SESSION_TOKEN_STORAGE_KEY, token)
  } else {
    sessionStorage.removeItem(SESSION_TOKEN_STORAGE_KEY)
  }
}

/**
 * Custom `axios` instance, used for all API requests. No base URL is set by
 * default, keeping relative paths unless a consumer overrides it.
 */
export const instance = axios.create()
instance.interceptors.request.use(sessionAuthInterceptor)
instance.interceptors.response.use(
  idleExpiresAtSuccessInterceptor,
  idleExpiresAtErrorInterceptor,
)
instance.interceptors.response.use(
  sessionTokenSuccessInterceptor,
  sessionTokenErrorInterceptor,
)

const IDLE_REMAINING_HEADER = "x-idle-seconds-remaining"

/**
 * @summary Forward the BE's idle-timeout countdown to the session module.
 *
 * @desc `SessionInactivityMiddleware` advertises seconds remaining before
 * idle expiry on every authenticated response via `X-Idle-Seconds-Remaining`.
 * The session module owns persistence and consumption of that value; these
 * interceptors only extract it from the HTTP layer.
 *
 * Both success and error paths capture the header: legitimate 4xx/5xx
 * responses (e.g. validation errors) still pass through the BE middleware,
 * which means they update `LAST_ACTIVITY` and carry the refreshed countdown
 * — dropping it would let the watcher's schedule drift toward stale.
 */
function idleExpiresAtSuccessInterceptor<T>(response: AxiosResponse<T>) {
  captureIdleRemaining(response.headers[IDLE_REMAINING_HEADER])
  return response
}

function idleExpiresAtErrorInterceptor(error: unknown) {
  if (axios.isAxiosError(error) && error.response) {
    captureIdleRemaining(error.response.headers[IDLE_REMAINING_HEADER])
  }
  return Promise.reject(error)
}

function captureIdleRemaining(header: unknown): void {
  if (typeof header !== "string") {
    return
  }
  const remainingSeconds = Number(header)
  if (Number.isFinite(remainingSeconds)) {
    recordIdleSecondsRemaining(remainingSeconds)
  }
}

/**
 * @summary Persist the `session_token` returned on any allauth flow response.
 *
 * @desc The embed runs under the APP client (see `getAuthClient`), whose
 * token strategy carries auth-flow state via the `X-Session-Token` header
 * rather than cookies. Multi-step flows (login by code, signup email
 * verification, password reset) return an updated `meta.session_token` on the
 * intermediate step that the next step MUST echo back, otherwise the BE has no
 * pending flow for the (anonymous) session and responds 409 Conflict.
 *
 * Capturing the token here keeps every step linked without each form having to
 * thread it through manually. The BROWSER client (getboundless.ai) relies on
 * cookies and omits `session_token`, so this is a no-op there.
 *
 * Both success and error paths are tapped because allauth signals a pending
 * flow as an HTTP 401 (`AuthenticationResponse`), which axios routes through
 * the error interceptor.
 *
 * Reference: https://docs.allauth.org/en/latest/headless/token-strategies/session-tokens.html
 */
function sessionTokenSuccessInterceptor<T>(response: AxiosResponse<T>) {
  captureSessionToken(response.data)
  return response
}

function sessionTokenErrorInterceptor(error: unknown) {
  if (axios.isAxiosError(error) && error.response) {
    captureSessionToken(error.response.data)
  }
  return Promise.reject(error)
}

function captureSessionToken(body: unknown): void {
  if (body && typeof body === "object" && "meta" in body) {
    const meta = (body as { meta?: { session_token?: unknown } }).meta
    if (meta && typeof meta.session_token === "string") {
      setSessionToken(meta.session_token)
    }
  }
}

let apiBaseUrl: string | undefined

/**
 * Allow runtime consumers (e.g. embeds) to provide an absolute base URL.
 * When unset, requests remain relative to the current origin.
 */
export function setApiBaseUrl(baseUrl?: string) {
  apiBaseUrl = baseUrl?.trim() || undefined
}

export function resetApiBaseUrl() {
  setApiBaseUrl(undefined)
}

export function getApiBaseUrl() {
  return apiBaseUrl
}

/**
 * Request interceptor to include Session token in headers.
 *
 * Also includes CSRF token.
 *
 * Even though we're executing on the browser, we might be running requests via
 * the Embed script (i.e. under another domain), so we can't rely only on cookies
 * for CSRF Protection.
 *
 * Reference: https://docs.allauth.org/en/latest/headless/token-strategies/session-tokens.html
 */
function sessionAuthInterceptor(config: InternalAxiosRequestConfig) {
  const token = sessionStorage.getItem(SESSION_TOKEN_STORAGE_KEY)

  if (token && config.headers) {
    config.headers["X-Session-Token"] = token
  }

  const csrfToken = Cookies.get(CSRF_COOKIE_NAME)

  if (csrfToken && config.headers) {
    config.headers["X-CSRFToken"] = csrfToken
  }

  return config
}

/**
 * Custom api client function provided to Orval, when building requests from
 * the OpenAPI schema.
 */
export const blApiClient = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig,
): Promise<T> => {
  const mergedConfig: AxiosRequestConfig = {
    withCredentials: true,
    baseURL: apiBaseUrl,
    ...config,
    ...options,
  }

  if (mergedConfig.baseURL === undefined) {
    delete mergedConfig.baseURL
  }

  return instance(mergedConfig).then(({ data }) => data)
}

export type ErrorType<Error> = AxiosError<Error>
export type BodyType<BodyData> = BodyData

/**
 * @summary Check if a generic error is of ErrorType.
 *
 * In effect, it's checking if it's an AxiosError, which translates into our
 * generic client ErrorType, used extensively by Orval/TanStack Query.
 *
 * This is specially useful when using try/catch.
 */
export function isClientError(error: unknown): error is ErrorType<unknown> {
  return (
    error instanceof Error && "isAxiosError" in error && !!error.isAxiosError
  )
}

/**
 * @summary Check if the error is a 404 response.
 */
export function isNotFoundError(error: unknown) {
  return isClientError(error) && error.status === 404
}

/**
 * @summary Determine which Auth client based on if running Embedded or not.
 *
 * This tells us whether it's possible to rely on Cookies for authentication or
 * not and determines which `django-allauth` headless URLs to use.
 */
export function getAuthClient(): Client {
  return isEmbed() ? Client.APP : Client.BROWSER
}
